/**
 * The naval rank ladder.
 *
 * **Rank is derived, never stored.** It is a pure function of the XP a player
 * has banked, so there is no column to migrate when the curve is retuned and no
 * way for a stored rank to disagree with the XP behind it — the same rule the
 * template states for stamina and cooldowns, applied to progression.
 *
 * Shared because both sides need it: the server has no reason to compute it at
 * all (it ships `rankXp` on the wire and lets the client name it), the battle
 * screen prints an abbreviation over every hull, and the harbour draws the bar
 * to the next rung. One table, one function, no drift.
 *
 * The curve is geometric: each rung costs 1.45× the one before, starting at 800
 * XP. A battle pays credits ÷ 10, so a good one is a few hundred XP — the first
 * rungs fall in an evening and the last is a long campaign. Thresholds are
 * rounded to readable numbers because a player reads them.
 *
 * **Rungs carry ids, never names.** A rank's name is a translation the frontend
 * owns; this file is shared with browser code that has no locale and with a
 * server that never sends prose. `rankNameKey`/`rankAbbrKey` build the keys.
 */

export interface NavalRank {
  /** 0-based position on the ladder. */
  index: number;
  /**
   * Stable identifier, and the only name this file knows.
   *
   * The display name and its abbreviation are translations, keyed
   * `ranks.<id>.name` and `ranks.<id>.abbr` in the frontend bundle
   * (`frontend/src/i18n/locales/`). Shared code cannot reach that bundle and
   * should not: the server sends `rankXp` and the screen names it.
   */
  id: string;
  /** XP at which this rank begins. */
  minXp: number;
}

export const RANKS: readonly NavalRank[] = [
  { index: 0, id: 'cadet', minXp: 0 },
  { index: 1, id: 'midshipman', minXp: 800 },
  { index: 2, id: 'ensign', minXp: 2_000 },
  { index: 3, id: 'sub_lieutenant', minXp: 3_650 },
  { index: 4, id: 'lieutenant', minXp: 6_100 },
  { index: 5, id: 'lieutenant_commander', minXp: 9_600 },
  { index: 6, id: 'commander', minXp: 14_750 },
  { index: 7, id: 'captain', minXp: 22_200 },
  { index: 8, id: 'senior_captain', minXp: 33_000 },
  { index: 9, id: 'flag_captain', minXp: 48_600 },
  { index: 10, id: 'commodore', minXp: 71_300 },
  { index: 11, id: 'rear_admiral', minXp: 104_000 },
  { index: 12, id: 'vice_admiral', minXp: 152_000 },
  { index: 13, id: 'admiral', minXp: 221_000 },
  { index: 14, id: 'admiral_of_the_fleet', minXp: 321_000 },
];

/** Translation key for a rank's full name. */
export function rankNameKey(id: string): string {
  return `ranks.${id}.name`;
}

/** Translation key for the short form the battle HUD prints over a hull. */
export function rankAbbrKey(id: string): string {
  return `ranks.${id}.abbr`;
}

export const TOP_RANK_INDEX = RANKS.length - 1;

export interface RankProgress extends NavalRank {
  /**
   * XP at which the next rank begins, or `null` at the top of the ladder —
   * `null` rather than Infinity so a caller that forgets to check renders
   * nothing instead of a bar that can never fill.
   */
  nextXp: number | null;
  /** Progress toward the next rank, 0–1. Always 1 at the top. */
  progress: number;
}

/**
 * The rank a player with this much XP holds. Negative, NaN and fractional XP
 * all resolve to a real rung — this is called from render paths that must never
 * throw.
 */
export function rankFromXp(xp: number): RankProgress {
  const banked = Number.isFinite(xp) ? Math.max(0, Math.floor(xp)) : 0;

  let index = 0;
  for (let i = RANKS.length - 1; i >= 0; i--) {
    if (banked >= RANKS[i]!.minXp) {
      index = i;
      break;
    }
  }

  const rank = RANKS[index]!;
  const next = index < TOP_RANK_INDEX ? RANKS[index + 1]! : null;
  const span = next ? next.minXp - rank.minXp : 0;
  const progress = next && span > 0 ? Math.min(1, (banked - rank.minXp) / span) : 1;

  return { ...rank, nextXp: next?.minXp ?? null, progress };
}

/** Just the short form's key, for the battle HUD. */
export function rankAbbr(xp: number): string {
  return rankAbbrKey(rankFromXp(xp).id);
}

/**
 * The rank a captain was promoted to when their XP moved from `before` to
 * `after`, or null when they stayed on their rung. A jump over several rungs
 * names the one they landed on. Used to word the inbox's promotion notice.
 */
export function promotedTo(before: number, after: number): NavalRank | null {
  const from = rankFromXp(before);
  const to = rankFromXp(after);
  return to.index > from.index ? to : null;
}
